import { useEffect, useState, useRef } from "react";
import { Upload, Trash2, Loader2, FileText, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { resolveImg, formatDate } from "@/lib/util";
import { PageTitle, inputCls } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminMedia() {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/media").then((r) => setMedia(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try { await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch (err) { toast.error(`${file.name}: ${apiErr(err.response?.data?.detail, "Upload failed")}`); }
    }
    toast.success("Upload complete");
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const del = async (id) => { if (!window.confirm("Delete this file?")) return; await api.delete(`/media/${id}`); toast.success("Deleted"); setSelected(null); load(); };
  const saveMeta = async () => { await api.put(`/media/${selected.id}`, { alt: selected.alt, caption: selected.caption, title: selected.title }); toast.success("Saved"); load(); };

  return (
    <div data-testid="admin-media">
      <PageTitle title="Media Library" subtitle="Upload and manage images and PDFs (JPG, PNG, WEBP, PDF)">
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-navy text-ivory px-4 py-2.5 rounded-sm text-sm inline-flex items-center gap-2 hover:bg-navy-light disabled:opacity-60" data-testid="media-upload-btn">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload Files
        </button>
        <input ref={fileRef} type="file" multiple className="hidden" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={upload} data-testid="media-file-input" />
      </PageTitle>

      {media.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg py-20 text-center text-slate-400">No media files yet. Upload images or PDFs to begin.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {media.map((m) => (
            <div key={m.id} className="group relative bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-brass cursor-pointer" onClick={() => setSelected({ ...m })} data-testid={`media-file-${m.id}`}>
              <div className="aspect-square bg-slate-50 flex items-center justify-center">
                {m.type === "pdf" ? <FileText size={40} className="text-slate-400" /> : <img src={resolveImg(m.url)} alt={m.alt} className="w-full h-full object-cover" />}
              </div>
              <div className="p-2"><p className="text-xs text-navy truncate">{m.title}</p></div>
              <button onClick={(e) => { e.stopPropagation(); del(m.id); }} className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>File Details</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="aspect-video bg-slate-50 rounded-sm overflow-hidden flex items-center justify-center">
                {selected.type === "pdf" ? <FileText size={48} className="text-slate-400" /> : <img src={resolveImg(selected.url)} alt="" className="w-full h-full object-contain" />}
              </div>
              <p className="text-xs text-slate-400">{selected.original_filename} · {(selected.size / 1024).toFixed(0)} KB · {formatDate(selected.created_at)}</p>
              <div><label className="text-xs font-semibold text-navy uppercase">Title</label><input className={inputCls} value={selected.title || ""} onChange={(e) => setSelected({ ...selected, title: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-navy uppercase">Alt Text</label><input className={inputCls} value={selected.alt || ""} onChange={(e) => setSelected({ ...selected, alt: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-navy uppercase">Caption</label><input className={inputCls} value={selected.caption || ""} onChange={(e) => setSelected({ ...selected, caption: e.target.value })} /></div>
              <div className="flex gap-2 pt-2">
                <button onClick={saveMeta} className="bg-brass text-navy font-semibold px-5 py-2 rounded-sm text-sm">Save</button>
                <button onClick={() => del(selected.id)} className="text-red-500 px-4 py-2 text-sm inline-flex items-center gap-1"><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
